<!--
  The drill-in grid every Insights card opens: the rows behind the card, filterable, sortable,
  groupable and pageable, with Units / Volume totals for the filtered rows and Export Excel.

  This is a port of the k-dialog + Grid block CGS2022 repeats in each analysis page (getData,
  getFilteredData, processFilteredData, exportExcel, columnReorder). The behaviour is the same,
  and it's written once here.

  mode="loans"    rows are loans: Units = the filtered row count, Volume = sum of `volumeProp`
                  (loanamount by default), as CGS's overviewDrilldown branch does.
  mode="summary"  rows are grouped figures: Units = sum of `count`, Volume = sum of `volume`,
                  as CGS's top-5 branch does.

  Row click: CGS opens its own loan page (/Loans/Detail/…/{id}). That only works on the CGS host,
  so here it opens there only when VUE_APP_CGS_URL is set, in a separate window.
-->
<template>
  <Dialog v-if="open" :title="title" class="ins-drill" :width="dialogWidth" :height="dialogHeight" @close="$emit('close')">
    <div class="ins-drill__bar">
      <div v-if="showTotals" class="ins-drill__totals">
        <span><b>Units</b>{{ fmtInt(units) }}</span>
        <span><b>Volume</b>{{ fmtCurrency(volume) }}</span>
        <span v-if="rowLink" class="ins-muted" style="color:#7b8397">Click a row to open the loan</span>
      </div>
      <button type="button" class="ins-drill__btn" :disabled="!filtered.length" @click="exportExcel">Export Excel</button>
    </div>
    <Grid :style="{ height: gridHeight }"
          :data-items="gridItems"
          :columns="gridColumns"
          :resizable="true"
          :reorderable="true"
          :sortable="true"
          :filterable="true"
          :groupable="true"
          :column-menu="true"
          :pageable="pageable"
          :filter="filter"
          :sort="sort"
          :group="group"
          :take="take"
          :skip="skip"
          :total="total"
          @columnreorder="e => (gridColumns = e.columns)"
          @datastatechange="onDataState"
          @rowclick="onRowClick" />
  </Dialog>
</template>

<script>
import { Dialog } from '@progress/kendo-vue-dialogs'
import { Grid } from '@progress/kendo-vue-grid'
import { process as kProcess, filterBy, orderBy } from '@progress/kendo-data-query'
import { saveExcel } from '@progress/kendo-vue-excel-export'
import { openLoanInWindow } from '@/helpers/openLoanWindow.js'
import { fmtInt, fmtCurrency, toNum } from '@/helpers/insights/insightMath.js'

// Columns a page passes without a width get one, so values ("Purchase", borrower names) read in full
// and the grid scrolls sideways instead of squeezing every column to a few letters.
const DEFAULT_COL_WIDTH = '160px'
function withWidths(cols) {
  return (cols || []).map(c => (c && !c.width ? { ...c, width: DEFAULT_COL_WIDTH } : c))
}

export default {
  name: 'DrillGridDialog',
  components: { Dialog, Grid },
  props: {
    open: { type: Boolean, default: false },
    title: { type: String, default: '' },
    items: { type: Array, default: () => [] },
    columns: { type: Array, default: () => [] },
    mode: { type: String, default: 'loans' },
    volumeProp: { type: String, default: 'loanamount' },
    exportName: { type: String, default: '' },
    // Off for grids whose rows are rates, not loans (Performance Scorecard).
    showTotals: { type: Boolean, default: true },
    // Off for loan-mode grids whose rows aren't loans (Baldwin's lead rows).
    openRows: { type: Boolean, default: true }
  },
  emits: ['close'],
  data() {
    return {
      gridColumns: [],
      filter: null,
      sort: [],
      group: [],
      take: 50,
      skip: 0,
      pageable: { buttonCount: 5, info: true, type: 'numeric', pageSizes: [25, 50, 100, 250], previousNext: true },
      winW: typeof window !== 'undefined' ? window.innerWidth : 1400,
      winH: typeof window !== 'undefined' ? window.innerHeight : 900
    }
  },
  computed: {
    // Full page: the dialog takes the whole window so the full row set fits (asked for 10/17).
    dialogWidth() { return this.winW },
    dialogHeight() { return this.winH },
    // Window minus the title bar (~64), the totals bar (~62) and the content padding (~40).
    gridHeight() { return Math.max(320, this.dialogHeight - 170) + 'px' },
    filtered() {
      return this.filter ? filterBy(this.items, this.filter) : this.items
    },
    processed() {
      return kProcess(this.filtered, { take: this.take, skip: this.skip, group: this.group, sort: this.sort })
    },
    gridItems() { return this.processed.data },
    total() { return this.processed.total },
    units() {
      if (this.mode === 'loans') return this.filtered.length
      return this.filtered.reduce((s, r) => s + (toNum(r.count) || 0), 0)
    },
    volume() {
      const prop = this.mode === 'loans' ? this.volumeProp : 'volume'
      return this.filtered.reduce((s, r) => s + (toNum(r[prop]) || 0), 0)
    },
    rowLink() { return this.openRows && this.mode === 'loans' && !!process.env.VUE_APP_CGS_URL }
  },
  watch: {
    open(v) { if (v) this.reset() },
    columns: { immediate: true, handler(c) { this.gridColumns = withWidths(c) } }
  },
  mounted() { window.addEventListener('resize', this.onResize) },
  beforeUnmount() { window.removeEventListener('resize', this.onResize) },
  methods: {
    fmtInt,
    fmtCurrency,
    reset() {
      this.filter = null
      this.sort = []
      this.group = []
      this.take = 50
      this.skip = 0
      this.gridColumns = withWidths(this.columns)
    },
    onResize() { this.winW = window.innerWidth; this.winH = window.innerHeight },
    onDataState(e) {
      const d = e.data
      this.filter = d.filter ?? null
      this.sort = d.sort || []
      this.group = d.group || []
      this.take = d.take
      this.skip = d.skip
    },
    exportExcel() {
      let data = this.filtered
      if (this.sort.length) data = orderBy(data, this.sort)
      saveExcel({ data, fileName: `${this.exportName || this.title || 'Insights'}.xlsx`, columns: this.gridColumns })
    },
    onRowClick(e) {
      if (!this.rowLink) return
      const id = e?.dataItem?.id
      if (!id) return
      const base = String(process.env.VUE_APP_CGS_URL).replace(/\/$/, '')
      const url = `${base}/Loans/Detail/00000000-0000-0000-0000-000000000000/${id}`
      if (!openLoanInWindow(url, e.event)) window.open(url, '_blank')
    }
  }
}
</script>
